import React, { memo, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Check, Sparkles, Loader2 } from 'lucide-react';

import { Modal } from './Modal';
import { usePremium } from '../../hooks';
import { useAuth } from '../../hooks';
import { getCheckoutUrl } from '../../services/lemonsqueezy';
import { FREEMIUM } from '../../constants';
import { allExtendedWords } from '../../data';

import type { PlanId } from '../../services/lemonsqueezy';

/**
 * Paywall — Custom paywall modal that displays the Lemon Squeezy subscription
 * plan and opens the hosted checkout in a new tab.
 *
 * With Lemon Squeezy, the purchase flow is:
 * 1. User clicks "Subscribe" → opens LS checkout URL (with Firebase UID in custom data)
 * 2. User completes payment on Lemon Squeezy's hosted page
 * 3. Webhook writes premium status to Firestore
 * 4. PremiumContext picks up the change via Firestore real-time listener
 * 5. Paywall auto-closes when premium becomes true
 *
 * Rendered once in `App.tsx` — visibility controlled by `PremiumContext`.
 *
 * @example
 * // In App.tsx — rendered globally
 * <Paywall />
 *
 * // To trigger from anywhere:
 * const { showPaywall } = usePremium();
 * showPaywall();
 */

export const Paywall: React.FC = memo(() => {
  const { t } = useTranslation();
  const {
    isPaywallVisible,
    hidePaywall,
    restorePurchase,
  } = usePremium();
  const { uid, user } = useAuth();

  const [isRestoring, setIsRestoring] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalWords = useMemo(() => allExtendedWords.length, []);

  const handleSubscribe = useCallback((planId: PlanId) => {
    if (!uid) {
      setError(t('premium.purchaseError', 'Purchase failed. Please try again.'));
      return;
    }

    setIsCheckingOut(true);
    setError(null);

    try {
      const checkoutUrl = getCheckoutUrl(planId, {
        uid,
        email: user?.email ?? undefined,
        name: user?.displayName ?? undefined,
      });

      // Open Lemon Squeezy checkout in a new tab
      window.open(checkoutUrl, '_blank');

      // Keep "checking out" state for a brief moment, then reset.
      // The Firestore listener in PremiumContext will auto-close the paywall
      // when the webhook writes the premium status.
      setTimeout(() => {
        setIsCheckingOut(false);
      }, 2000);
    } catch (_error) {
      setError(t('premium.purchaseError', 'Purchase failed. Please try again.'));
      setIsCheckingOut(false);
    }
  }, [uid, user?.email, user?.displayName, t]);

  const handleRestore = useCallback(async () => {
    setIsRestoring(true);
    setError(null);

    try {
      await restorePurchase();
      // If restore found premium, PremiumContext will set isPremium=true
      // and auto-close the paywall. If not, nothing changes.
    } catch (_error) {
      setError(t('premium.restoreError', 'Could not restore purchase. Please try again.'));
    } finally {
      setIsRestoring(false);
    }
  }, [restorePurchase, t]);

  // Reset state when paywall visibility changes
  const handleClose = useCallback(() => {
    setError(null);
    setIsCheckingOut(false);
    setIsRestoring(false);
    hidePaywall();
  }, [hidePaywall]);

  return (
    <Modal
      isOpen={isPaywallVisible}
      onClose={handleClose}
      size="lg"
      closeOnBackdrop={!isCheckingOut}
      showCloseButton={!isCheckingOut}
      className="!p-0 overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-kid-purple via-kid-blue to-kid-green p-5 sm:p-6 text-center text-white">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Crown className="w-7 h-7 sm:w-8 sm:h-8 text-kid-yellow" />
          <h2 className="text-xl sm:text-2xl font-bold font-heebo">
            {t('premium.title')}
          </h2>
          <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-kid-yellow" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Features comparison */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {/* Free tier */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-dark-muted mb-2 uppercase tracking-wide">
              {t('premium.freeIncludes')}
            </p>
            <ul className="space-y-1.5 text-xs sm:text-sm text-gray-600 dark:text-dark-muted">
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <span>{t('premium.freeFeatures.smartMix')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <span>{t('premium.freeFeatures.limitedWords', { count: FREEMIUM.FREE_WORD_LIMIT })}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <span>{t('premium.freeFeatures.oneCollection')}</span>
              </li>
            </ul>
          </div>

          {/* Premium tier */}
          <div className="rounded-xl border-2 border-kid-purple bg-kid-purple/5 dark:bg-kid-purple/10 p-3">
            <p className="text-xs font-semibold text-kid-purple mb-2 uppercase tracking-wide">
              {t('premium.premiumIncludes')}
            </p>
            <ul className="space-y-1.5 text-xs sm:text-sm text-gray-800 dark:text-dark-text">
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-kid-green mt-0.5 shrink-0" />
                <span>{t('premium.features.allWords', { count: totalWords })}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-kid-green mt-0.5 shrink-0" />
                <span>{t('premium.features.allModes')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-kid-green mt-0.5 shrink-0" />
                <span>{t('premium.features.allStickers')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-kid-green mt-0.5 shrink-0" />
                <span>{t('premium.features.cloudSync')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Subscribe button (monthly plan) */}
        <div className="space-y-2.5 mb-4">
          <button
            onClick={() => handleSubscribe('monthly')}
            disabled={isCheckingOut || isRestoring}
            className={`
              w-full flex items-center justify-between
              rounded-xl border-2 border-kid-purple bg-kid-purple/5 dark:bg-kid-purple/10 shadow-md
              p-3 sm:p-4
              transition-all duration-200
              ${isCheckingOut || isRestoring ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg active:scale-[0.98]'}
            `}
          >
            <div className="flex items-center gap-3">
              <span className="text-sm sm:text-base font-medium text-gray-800 dark:text-dark-text">
                {t('premium.packageTitle.monthly', 'Monthly')}
              </span>
            </div>
            <div className="text-right">
              <span className="text-base sm:text-lg font-bold text-kid-purple">
                {t('premium.packagePrice.monthly', FREEMIUM.PRICE_DISPLAY)}
              </span>
              <span className="text-xs text-gray-500 dark:text-dark-muted ml-1">
                {t('premium.periods.monthly', '/ month')}
              </span>
            </div>
          </button>
        </div>

        {/* Checking out message */}
        {isCheckingOut && (
          <p className="text-center text-sm text-kid-blue mb-3 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('premium.checkingOut', 'Complete your purchase in the new tab...')}
          </p>
        )}

        {/* Error message */}
        {error && (
          <p className="text-center text-sm text-kid-red mb-3">
            {error}
          </p>
        )}

        {/* Restore purchase */}
        <div className="text-center">
          <button
            onClick={handleRestore}
            disabled={isCheckingOut || isRestoring}
            className="text-xs sm:text-sm text-kid-purple hover:text-kid-purple/80 underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRestoring ? (
              <span className="flex items-center gap-1.5 justify-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('premium.restoring', 'Restoring...')}
              </span>
            ) : (
              t('premium.restoreButton')
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
});

Paywall.displayName = 'Paywall';
